import { useState, useEffect } from "react";
import { Link } from "wouter";
import ProductCard from "@/components/product-card";
import { HERO_SLOGANS, COLLECTION_ITEMS } from "@/lib/constants";
import heroImage from "@assets/image_1754380186676.png";

export default function Home() {
  const [currentSlogan, setCurrentSlogan] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlogan((prev) => (prev + 1) % HERO_SLOGANS.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const featuredProducts = COLLECTION_ITEMS.slice(0, 3);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 hero-overlay bg-cover bg-center opacity-40"
          style={{ backgroundImage: `url(${heroImage})` }}
        />
        <div className="relative z-10 text-center text-white px-4 fade-in">
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold mb-6">
            <span className="block">
              {HERO_SLOGANS[currentSlogan].split(' ').slice(0, -2).join(' ')}
            </span>
            <span className="text-[var(--perahan-gold)]">
              {HERO_SLOGANS[currentSlogan].split(' ').slice(-2).join(' ')}
            </span>
          </h1>
          <p className="text-xl text-gray-600 md:text-2xl mb-8 max-w-3xl mx-auto leading-relaxed">
            Tradition woven into every thread. From artisan hands to your wardrobe.
          </p>
          <Link href="/collection">
            <button className="btn-terracotta text-lg shadow-lg hover:scale-105">
              Explore Our Collection
            </button>
          </Link>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="fade-in">
              <img 
                src="https://pixabay.com/get/gcdfe362bdcc1e8f77fd8b586853b9016aa7a856abea3570af1156f872abf46d76a7b211d15e8670f348fd353fbb66c20a6ca09d94da30cfba652b2e862ec6cf1_1280.jpg"
                alt="Traditional block printing artisan at work" 
                className="rounded-lg shadow-lg w-full h-auto"
                loading="lazy"
              />
            </div>
            <div className="fade-in">
              <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-6">
                A Legacy of <span className="text-[var(--perahan-maroon)]">Craftsmanship</span>
              </h2>
              <p className="text-lg mb-6 leading-relaxed">
                Perahan has celebrated the art of hand-block printing since 1983. Our garments are designed with soul, printed with tradition, and stitched with care. Every piece tells a story — of culture, of craft, and of timeless style.
              </p>
              <Link href="/about">
                <button className="btn-primary">
                  Read Our Story
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-4">
              Featured Collection
            </h2>
            <p className="text-lg text-[var(--perahan-gray)]">
              Handcrafted pieces that celebrate heritage and elegance
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {featuredProducts.map((product) => (
              <ProductCard
                key={product.id}
                title={product.title}
                description={product.description}
                image={product.image}
              />
            ))}
          </div>

          <div className="text-center">
            <Link href="/collection">
              <button className="btn-primary text-lg px-8 py-3">
                View Complete Collection
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-[var(--perahan-ivory)]">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-4">
              What Our Customers Say
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Ayesha K.",
                review: "The quality and craftsmanship of Perahan's clothing is exceptional. Each piece truly tells a story of traditional artistry."
              },
              {
                name: "Fatima S.",
                review: "I love how Perahan preserves our cultural heritage while creating modern, wearable fashion. Absolutely beautiful work!"
              },
              {
                name: "Zara M.",
                review: "The attention to detail and authentic block printing techniques make every Perahan piece a treasure. Highly recommended!"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-lg shadow-lg">
                <div className="text-[var(--perahan-gold)] mb-4">
                  {'★'.repeat(5)}
                </div>
                <p className="text-[var(--perahan-gray)] mb-4 italic">
                  "{testimonial.review}"
                </p>
                <p className="font-semibold text-[var(--perahan-charcoal)]">
                  — {testimonial.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
